import { CSSProperties } from 'react';

interface SkeletonLoaderProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  className?: string;
  style?: CSSProperties;
}

export function SkeletonLoader({
  width = '100%',
  height = '20px',
  borderRadius = '0.375rem',
  className = '',
  style = {}
}: SkeletonLoaderProps) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 75%)',
        backgroundSize: '200% 100%',
        animation: 'skeleton-shimmer 1.5s infinite',
        ...style
      }}
    />
  );
}

export function SkeletonText({
  lines = 1,
  width = '100%',
  gap = '0.5rem'
}: {
  lines?: number;
  width?: string | string[];
  gap?: string;
}) {
  const widths = Array.isArray(width) ? width : Array(lines).fill(width);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLoader
          key={i}
          height="16px"
          width={widths[i] || widths[0]}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ theme = 'dark' }: { theme?: 'dark' | 'light' }) {
  const cardBg = theme === 'dark'
    ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)'
    : '#ffffff';
  const cardBorder = theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb';

  return (
    <div style={{
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <SkeletonLoader height="14px" width="60%" style={{ marginBottom: '0.75rem' }} />
      <SkeletonLoader height="32px" width="40%" />
    </div>
  );
}

export function SkeletonTable({
  rows = 5,
  columns = 6,
  theme = 'dark'
}: {
  rows?: number;
  columns?: number;
  theme?: 'dark' | 'light';
}) {
  const tableBg = theme === 'dark' ? 'rgba(15, 15, 20, 0.4)' : '#ffffff';
  const cardBorder = theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb';
  const headerBg = theme === 'dark' ? 'rgba(26, 26, 46, 0.6)' : '#f9fafb';

  return (
    <div style={{
      background: tableBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: '0.5rem',
      overflow: 'hidden',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: headerBg }}>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>
                <SkeletonLoader height="12px" width={i === 0 ? '80%' : '60%'} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} style={{ borderTop: `1px solid ${cardBorder}` }}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} style={{ padding: '1rem' }}>
                  <SkeletonLoader
                    height="16px"
                    width={colIndex === 0 ? '90%' : colIndex === columns - 1 ? '70px' : '70%'}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonAvatar({ size = '2rem' }: { size?: string }) {
  return (
    <SkeletonLoader
      width={size}
      height={size}
      borderRadius="50%"
    />
  );
}

export function SkeletonButton({ width = '80px', height = '36px' }: { width?: string; height?: string }) {
  return (
    <SkeletonLoader
      width={width}
      height={height}
      borderRadius="0.375rem"
    />
  );
}
